/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useContext } from 'react';
import ReactFlagsSelect from 'react-flags-select';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { ctrlDefaults, getCountriesList } from '../../../../shared/form-lib';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import { getStateValue } from '../../../form/utils';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Field,
} from '../../common-field-styles';

const countries = getCountriesList();

const getCountryProp = (val, prop1, prop2) => countries.find((c) => c[prop1] === val)[prop2];

const getCountry = (ctrl, tr, col, inTable) => {
  if (inTable) {
    return ctrl?.value?.[tr - 1]?.[col - 1]
      ? getCountryProp(ctrl?.value?.[tr - 1]?.[col - 1], 'name', 'code')
      : ctrl.props.default;
  }
  return ctrl.value ? getCountryProp(ctrl.value, 'name', 'code') : ctrl.props.default;
};

const Country = ({ ctrl, inTable, col, tr, formDesign }) => {
  const dispatch = useDispatch();
  const { inCustomise, inBuilder, inForm, currentPageNo } = useContext(FormContext);
  const { inPreviewPage: inBuilderPreview } = useSelector((state) => state.builder.present);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];

  const [selected, setSelected] = useState(getCountry(ctrl, tr, col, inTable));
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );

  const saveUpdatedCtrl = (newValue) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property: 'value',
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
        ...(inTable && { tr: tr - 1, col: col - 1 }),
      })
    );
  };

  const handleSelect = (code) => {
    if (!inForm) return;

    setSelected(code);
    const country = getCountryProp(code, 'code', 'name');
    if (country) saveUpdatedCtrl(country);
  };

  // Watch for change in default prop on the builder and logic reset 
  useEffect(() => {
    const code = getCountry(ctrl, tr, col, inTable);
    setSelected(code);

    if (inForm && ctrl.props.default && !ctrl.value) {
      const country = getCountryProp(code, 'code', 'name');
      if (country) saveUpdatedCtrl(country);
    }
  }, [inForm, ctrl.props.default]);

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  return (
    <>
      <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
        <FieldWrapper>
          {ctrlValidationError && (
            <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
          )}

          {!inTable ? (
            <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
              <LabelWrapper
                notInBuilder={!inBuilder}
                required={ctrl.props.required}
                className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
                <label dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
                <SpecialPropsIndicators ctrl={ctrl} />
              </LabelWrapper>

              <InputWrapper
                full
                notInBuilder={!inBuilder}
                className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
                <ReactFlagsSelect
                  fullWidth
                  searchable
                  selected={selected}
                  id={`fpl-${ctrl.internal_id}`}
                  searchPlaceholder="Search countries"
                  onSelect={(code) => handleSelect(code)}
                  placeholder={ctrl.props.placeholder || 'Select Country'}
                  disabled={ctrl.props.readonly || (inBuilder && !inBuilderPreview) || inCustomise}
                />

                <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                  {ctrl.props.subtext}
                </SubtextWrapper>
              </InputWrapper>
            </Field>
          ) : (
            <Field>
              <InputWrapper full notInBuilder={!inBuilder}>
                <ReactFlagsSelect
                  fullWidth
                  searchable
                  selected={selected}
                  searchPlaceholder="Search countries"
                  id={`fpl-${col}-${ctrl.internal_id}`}
                  onSelect={(code) => handleSelect(code)}
                  placeholder={ctrl.table_props[col - 1].placeholder || 'Select Country'}
                  disabled={ctrl.props.readonly || (inBuilder && !inBuilderPreview) || inCustomise}
                />
              </InputWrapper>
            </Field>
          )}
        </FieldWrapper>
      </MainfieldWrapper>
    </>
  );
};

Country.defaultProps = {
  col: null,
  tr: null,
  inTable: false,
  formDesign: null,
};

Country.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  col: PropTypes.number,
  tr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inTable: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default Country;
